import { useContext, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import Button from "../components/button.jsx";
import Input from "../components/input.jsx";
import MarkdownDisplay from "../components/markdownDisplay.jsx";
import MarkdownImageInsertButton from "../components/markdownImageInsertButton.jsx";
import NewsTranslationEditor from "../components/newsTranslationEditor.jsx";
import PageHero from "../components/pageHero.jsx";
import { NewsContext } from "../context/NewsContext";
import { UserContext } from "../context/UserContext.jsx";
import { formatPublishDate } from "../dateTimeUtils.js";
import { useLocalePath } from "../hooks/useLocalePath";
import { deleteNews, toggleNewsPublished, updateNewsArticle } from "../httpService.js";
import { absoluteUrl, DEFAULT_COMPANY_NAME, LOGO_URL, ORGANIZATION_ID } from "../seoUtils";

const News = () => {
  const { id } = useParams();
  const { locale, localePath } = useLocalePath();
  const { news, reinitialize } = useContext(NewsContext);
  const { hasRole } = useContext(UserContext);
  const navigate = useNavigate();
  const /**@type NewsArticle*/ article = news.find(
      (article) => article.id === Number.parseInt(id, 10)
    );

  const [sendingRequest, setSendingRequest] = useState(false);
  const [titleError, setTitleError] = useState(null);
  const [imageError, setImageError] = useState(null);
  const bodyRef = useRef(null);

  const [imageFile, setImageFile] = useState(null);
  const [newsData, setNewsData] = useState({
    id: 0,
    authorName: "",
    title: "",
    imageUrl: "",
    body: "",
    published: false,
    createdAt: "",
  });

  useEffect(() => {
    if (article) {
      setNewsData({
        id: article.id || 0,
        authorName: article.authorName || "",
        title: article.title || "",
        imageUrl: article.imageUrl || "",
        body: article.body || "",
        published: article.published,
        createdAt: article.createdAt || "",
      });
    }
  }, [article]);

  const handleImageChange = (e) => {
    const newImage = e.target.files[0];
    setImageFile(newImage);
  };

  const handleInputChange = (e) => {
    setNewsData({ ...newsData, [e.target.name]: e.target.value });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSendingRequest(true);
    let hasErrors = false;

    if (!newsData.title) {
      setTitleError("Titel krävs");
      hasErrors = true;
    } else {
      setTitleError(null);
    }

    if (imageFile && imageFile.type !== "image/jpeg") {
      setImageError("Endast jpg (jpeg) bilder är tillåtna");
      hasErrors = true;
    } else {
      setImageError(null);
    }

    if (hasErrors) {
      setSendingRequest(false);
      return;
    }

    try {
      setSendingRequest(true);
      await updateNewsArticle(imageFile, article.id, newsData);
      reinitialize();
      alert("Nyhet uppdaterad");
      setSendingRequest(false);
    } catch (_error) {
      alert("Kunde inte uppdatera maskin");
      setSendingRequest(false);
    }
  };

  /** @param article {NewsArticle} */
  function formatAuthorNameAndPublishDate(article) {
    if (article.authorName === "") {
      return `Publicerad ${formatPublishDate(article.createdAt)}`;
    }
    return `Publicerad av ${article.authorName} ${formatPublishDate(article.createdAt)}`;
  }

  /** @param article {NewsArticle} */
  const handleDeleteNews = async (article) => {
    try {
      if (!window.confirm("Är du säker på att du vill ta bort den här nyhetsartikeln?")) {
        return;
      }
      await deleteNews([article.id]);
      alert("Artikel borttagen!");
      reinitialize();
      navigate(-1);
    } catch (_error) {
      alert("Kunde inte radera artikel");
    }
  };

  /**@param article {NewsArticle} */
  const handleTogglePublishNews = async (article) => {
    try {
      const publish = !article.published;
      await toggleNewsPublished(article.id, publish);
      alert(`Artikel ${publish ? "publicerad" : "avpublicerad"}`);
      reinitialize();
    } catch (_error) {
      alert("Kunde inte ändra artikelns publicitet");
    }
  };

  return (
    article && (
      <>
        <Helmet>
          <title>AS Maskiner - {article.title}</title>
          <meta
            name="description"
            content={article.body?.replace(/[#*_`~>[\]]/g, "").slice(0, 200)}
          />
          <meta property="og:url" content={absoluteUrl(locale, `/news/${article.id}`)} />
          <meta property="og:title" content={article.title} />
          {article.imageUrl && <meta property="og:image" content={article.imageUrl} />}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "NewsArticle",
              url: absoluteUrl(locale, `/news/${article.id}`),
              mainEntityOfPage: absoluteUrl(locale, `/news/${article.id}`),
              headline: article.title,
              description: article.body?.replace(/[#*_`~>[\]]/g, "").slice(0, 200),
              ...(article.imageUrl ? { image: [article.imageUrl] } : {}),
              ...(article.createdAt
                ? { datePublished: new Date(article.createdAt).toISOString() }
                : {}),
              ...(article.updatedAt
                ? { dateModified: new Date(article.updatedAt).toISOString() }
                : {}),
              ...(article.authorName
                ? { author: { "@type": "Person", name: article.authorName } }
                : {}),
              publisher: {
                "@type": "Organization",
                "@id": ORGANIZATION_ID,
                name: DEFAULT_COMPANY_NAME,
                logo: { "@type": "ImageObject", url: LOGO_URL },
              },
            })}
          </script>
        </Helmet>
        <PageHero
          titleKey="news.hero_title"
          titleFallback="Nyheter"
          subtitleKey="news.hero_subtitle"
          backgroundKey="news.hero_background"
          alignment="bottom"
        />
        <div className="container mx-auto px-4 sm:px-8 py-8 max-w-4xl">
          <img
            src={article.imageUrl}
            alt="Article"
            className="w-auto max-w-full mx-auto mb-8 max-h-[450px]"
          />
          <Link to={localePath("/news")} className="font-bold">
            ← Tillbaka till nyheter
          </Link>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 mt-4">
            {article.title} {article.published ? "" : "(ej publicerad)"}
          </h1>
          <p className="text-text-muted text-sm mb-8">{formatAuthorNameAndPublishDate(article)}</p>
          <MarkdownDisplay data={article.body} />

          {hasRole("admin") && (
            <>
              <form
                onSubmit={handleSave}
                className="flex flex-col gap-8 border border-primary p-4 mt-4"
              >
                <h3 className="text-xl font-bold w-fit">Ändra information</h3>
                <div>
                  <Input
                    id="title"
                    label="Titel"
                    type="text"
                    name="title"
                    value={newsData.title}
                    onChange={handleInputChange}
                    error={titleError}
                    required
                  />
                </div>
                <div>
                  <Input
                    id="image"
                    name="image"
                    label="Ändra bild"
                    type="file"
                    accept="image/jpg"
                    onChange={handleImageChange}
                    error={imageError}
                  />
                </div>
                <div>
                  <label htmlFor="body" className="block text-text-body text-sm font-bold mb-2">
                    Innehåll{" "}
                    <a
                      href="https://www.markdownguide.org/cheat-sheet/"
                      target="_blank"
                      className="text-primary300"
                      rel="noopener"
                    >
                      (markdown)
                    </a>
                  </label>
                  <div className="mb-2">
                    <MarkdownImageInsertButton
                      textareaRef={bodyRef}
                      value={newsData.body}
                      onChange={(v) => setNewsData({ ...newsData, body: v })}
                    />
                  </div>
                  <textarea
                    id="body"
                    name="body"
                    ref={bodyRef}
                    value={newsData.body}
                    onChange={handleInputChange}
                    rows="6"
                    required
                    className="appearance-none border border-border-default w-full py-2 px-3 text-text-body leading-tight"
                  />
                  <p className="block text-text-body text-sm font-bold mb-2 pt-4">
                    Förhandsvisning
                  </p>
                  <MarkdownDisplay sx="border p-4" data={newsData.body} />
                </div>
                <div>
                  <Input
                    id="authorName"
                    name="authorName"
                    type="text"
                    label="Författare"
                    value={newsData.authorName}
                    onChange={handleInputChange}
                    className="border border-border-default w-full p-2 rounded"
                  />
                </div>
                <div className="space-x-4 flex">
                  <Button type="submit" loading={sendingRequest}>
                    Spara
                  </Button>
                  <div>
                    <Button
                      onClick={() => {
                        handleDeleteNews(article);
                      }}
                    >
                      Radera nyhet
                    </Button>
                  </div>
                  <div>
                    <Button type="button" onClick={() => handleTogglePublishNews(article)}>
                      {article.published ? "Avpublicera nyhet" : "Publicera nyhet"}
                    </Button>
                  </div>
                </div>
              </form>

              <div className="border border-primary p-4 mt-4">
                <NewsTranslationEditor newsId={article.id} reinitialize={reinitialize} />
              </div>
            </>
          )}
        </div>
      </>
    )
  );
};

export default News;
